import { InsuranceType } from "../../../../../type/Appointment.js";
import * as Yup from "yup";
import { MaskedDateInput } from "../../components/masked-date-input/masked-date-input.js";
import { isPatientUnderaged, hasFamilyInsuranceData } from "../entities/Form.js";
import { createFields } from "../helpers/createFields.js";
import { resetFields } from "../helpers/resetFields.js";
import { maxAgeValid, dateValidation, adultValid, addressValidation } from "../yup/yup.js";
import "react/jsx-runtime";
import "react-i18next";
import "react-text-mask";
import "../../../ana-toolbox/ana-toolbox.js";
import "../../../../../constants/date/date.js";
import "date-fns/fp/format";
import "date-fns/isValid";
import "date-fns/parse";
import "date-fns";
import "../helpers/toBoolean.js";
import "../../../../../utils/pipe.js";
import "date-fns/fp";
import "libphonenumber-js/max";
import "libphonenumber-js/mobile";
import "../helpers/predicates.js";
import "../yup/emailValidation.js";
Yup.addMethod(Yup.string, "maxAge", maxAgeValid);
Yup.addMethod(Yup.string, "birthday", dateValidation);
Yup.addMethod(Yup.string, "adult", adultValid);
Yup.addMethod(Yup.string, "address", addressValidation);
const genderOptions = [
  { label: "option.female", value: "FEMALE" },
  { label: "option.male", value: "MALE" },
  { label: "option.diverse", value: "DIVERSE" }
];
const isFilledForADifferentPerson = (values) => Boolean(values.filedForDifferentPerson);
const personalInformationFields = createFields({
  filedForDifferentPerson: {
    __type: "toggle",
    label: "field.anamnesis_for_another_person_label",
    name: "filedForDifferentPerson",
    helperText: "field.anamnesis_for_another_person_helper",
    hooks: {
      beforeChange: (value, helpers, _event) => {
        if (typeof value !== "boolean")
          return;
        if (value)
          return;
        resetFields(
          [
            "contactPersonFirstName",
            "contactPersonLastName",
            "contactPersonBirthday",
            "contactPersonCity",
            "contactPersonStreet",
            "contactPersonZipcode"
          ],
          helpers
        );
      }
    },
    visibleWhen: (values) => {
      if (isPatientUnderaged(values))
        return true;
      return values.bookedForDifferentPerson !== false;
    },
    validation: Yup.boolean().when("birthday", {
      is: (birthday) => isPatientUnderaged({ birthday }),
      then: Yup.boolean().oneOf([true], "field.filled_for_different_person").required("field.filled_for_different_person"),
      otherwise: Yup.boolean()
    })
  },
  contactPersonFirstName: {
    __type: "text",
    label: "field.contact_person_first_name_label",
    name: "contactPersonFirstName",
    isRequired: true,
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string().when("filedForDifferentPerson", {
      is: true,
      then: Yup.string().required("field.contact_person_first_name_error"),
      otherwise: Yup.string()
    })
  },
  contactPersonLastName: {
    __type: "text",
    label: "field.contact_person_last_name_label",
    name: "contactPersonLastName",
    isRequired: true,
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string().when("filedForDifferentPerson", {
      is: true,
      then: Yup.string().required("field.contact_person_last_name_error"),
      otherwise: Yup.string()
    })
  },
  contactPersonBirthday: {
    __type: "text",
    label: "field.date_of_birth",
    name: "contactPersonBirthday",
    isRequired: true,
    inputProps: {
      inputComponent: MaskedDateInput,
      InputLabelProps: {
        shrink: true
      }
    },
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string().when("filedForDifferentPerson", {
      is: true,
      then: Yup.string().birthday({ empty: "birthday_form_error", invalidDate: "invalid_date", inPast: "in_past" }).adult("legal_representative_valid_age").required(),
      otherwise: Yup.string()
    })
  },
  contactPersonCity: {
    __type: "text",
    label: "field.city",
    name: "contactPersonCity",
    isRequired: true,
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string().when("filedForDifferentPerson", {
      is: true,
      then: Yup.string().required("city_error"),
      otherwise: Yup.string()
    }),
    hooks: {
      beforeChange: (_value, helpers, _event) => {
        helpers.setFieldValue("contactAddressGoogleVerified", false);
      }
    }
  },
  contactPersonStreet: {
    __type: "address",
    label: "field.street_and_number",
    name: "contactPersonStreet",
    isRequired: true,
    inputProps: {
      autoFocus: true
    },
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string().when("filedForDifferentPerson", {
      is: true,
      then: Yup.string().address("invalid_address_error").required("street_and_number_error"),
      otherwise: Yup.string()
    })
  },
  contactPersonZipcode: {
    __type: "text",
    label: "field.zipCode",
    name: "contactPersonZipcode",
    isRequired: true,
    validation: Yup.string().when("filedForDifferentPerson", {
      is: true,
      then: Yup.string().required("zipCode_error"),
      otherwise: Yup.string()
    }),
    hooks: {
      beforeChange: (_value, helpers, _event) => {
        helpers.setFieldValue("contactAddressGoogleVerified", false);
      }
    },
    visibleWhen: isFilledForADifferentPerson
  },
  contactPersonRelation: {
    __type: "text",
    label: "field.contact_person_relation_label",
    name: "contactPersonRelation",
    visibleWhen: isFilledForADifferentPerson
  },
  visitReason: {
    __type: "text",
    label: "field.visit_reason_label",
    name: "visitReason",
    helperText: "field.visit_reason_helper"
  },
  gender: {
    __type: "radioGroup",
    label: "field.gender",
    name: "gender",
    isRequired: true,
    options: genderOptions,
    validation: Yup.string().oneOf(["MALE", "FEMALE", "DIVERSE"]).required("gender_error"),
    hooks: {
      beforeChange: (gender, { setFieldValue }, _event) => {
        if (typeof gender === "string" && gender === "MALE") {
          setFieldValue("isPregnant", void 0);
          setFieldValue("pregnantMonths", void 0);
        }
      }
    }
  },
  firstName: {
    __type: "text",
    label: "field.first_name",
    name: "firstName",
    isRequired: true,
    validation: Yup.string().required("name_error")
  },
  lastName: {
    __type: "text",
    label: "field.last_name",
    name: "lastName",
    isRequired: true,
    validation: Yup.string().required("name_error")
  },
  birthday: {
    __type: "text",
    label: "field.date_of_birth",
    name: "birthday",
    isRequired: true,
    inputProps: {
      type: "text",
      inputComponent: MaskedDateInput,
      InputLabelProps: {
        shrink: true
      }
    },
    validation: Yup.string().birthday({ empty: "birthday_form_error", invalidDate: "invalid_date", inPast: "in_past" }).maxAge("max_age").required(),
    hooks: {
      beforeChange: (value, { setFieldValue, getFieldProps }, _event) => {
        if (typeof value !== "string")
          return;
        const holderFirstName = getFieldProps("holderFirstName").value;
        const holderLastName = getFieldProps("holderLastName").value;
        const holderCity = getFieldProps("holderCity").value;
        const holderStreet = getFieldProps("holderStreet").value;
        const holderZipcode = getFieldProps("holderZipcode").value;
        const holderBirthday = getFieldProps("holderBirthday").value;
        const insuranceType = getFieldProps("type").value;
        if (isPatientUnderaged({ birthday: value }) && insuranceType === InsuranceType.SOCIAL_INSURANCE) {
          setFieldValue("hasFamilyInsurance", false);
        } else if (!hasFamilyInsuranceData({
          holderFirstName,
          holderLastName,
          holderCity,
          holderStreet,
          holderZipcode,
          holderBirthday
        })) {
          setFieldValue("hasFamilyInsurance", void 0);
        }
      }
    }
  },
  placeOfBirth: {
    __type: "text",
    label: "field.place_of_birth",
    name: "placeOfBirth",
    isRequired: true,
    validation: Yup.string().required("place_of_birth_error"),
    helperText: "field.place_of_birth_helper"
  }
});
const buildSchema = (fields) => {
  return Object.fromEntries(
    Object.entries(fields).map(([key, settings]) => [key, settings.validation]).filter(([_, validation]) => Boolean(validation))
  );
};
const personalInformationValidationSchema = Yup.object().shape(buildSchema(personalInformationFields));
export {
  personalInformationFields,
  personalInformationValidationSchema
};
